import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Portal } from 'src/components';
import { isNativePlatform } from '../../../config';

import hamburgerIcon from './images/hamburger.svg';
import hamburgerIconMobile from './images/hamburger_mobile.svg';
import closeIcon from './images/close.svg';
import closeIconMobile from './images/close_mobile.svg';

import UserMenu from '../UserMenu';
import BrandLogo from '../BrandLogo';
import { ContainedWidth } from '../Layout';
import { useMenuItems, PyMenuItem } from '../hooks';
import { isExternalLink, isPathActive, findActiveOwnerLabel } from './utils';

const SAFE_TOP = 'env(safe-area-inset-top, 0px)';

const Spacer = styled.div`
  height: calc(64px + ${SAFE_TOP});
`;

const Wrapper = styled.div`
  border-top: 4px solid ${p => p.theme.palette.primary.main};
  position: fixed;
  top: 0;
  width: 100%;
  max-width: 100vw;
  height: calc(64px + ${SAFE_TOP});
  padding-top: ${SAFE_TOP};
  z-index: 1001;
  background: ${p => p.theme.palette.common.white};
  ${p => p.theme.shadows[1]}
`;

const Content = styled.div`
  height: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Logo = styled(BrandLogo)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const Hamburger = styled.button<{open: boolean, mobile?: boolean}>`
  width: 50px;
  height: 60px;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  -webkit-tap-highlight-color: transparent;
  background-image: url(${
    ({open, mobile}) =>
            open ?
                    mobile ? closeIconMobile : closeIcon :
                    mobile ? hamburgerIconMobile : hamburgerIcon
    });
  background-size: 32px 32px;
  background-repeat: no-repeat;
  background-position: center;
  transform: translateX(8px);
`;

const Slideable = styled.div<{open: boolean}>`
  position: fixed;
  z-index: 1000;
  top: calc(64px + ${SAFE_TOP});
  left: 0;
  bottom: 0;
  right: 0;
  background: ${p => p.theme.palette.common.white};
  transition: transform 300ms;
  transform: translateY(${p => p.open ? 0 : -100}%);
  overflow: scroll;
`;

const UserMenuWrapper = styled.div`
  padding: 24px 0 8px;
`;

const Menu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px 24px;
`;

const Link = styled(NavLink).attrs({ className: 'nav-item' })`
  display: block;
  line-height: 24px;
  font-size: 16px;
  padding: 8px;
  border-radius: 4px;
  && {
    color: ${p => p.theme.palette.common.black};
    text-decoration: none;
  }
  &.active {
    background: #f3fbfc;
  }
`;

const FeaturedLink = styled(Link)`
  && {
    color: ${p => p.theme.palette.secondary.main};
  }
`;

function NavMenuItem({
  menuItem,
  onClick,
  isChildActive,
  pathname,
}: {
  menuItem: PyMenuItem;
  onClick: () => void;
  isChildActive: boolean;
  pathname: string;
}) {
  const { path, label, children, isFeatured } = menuItem;
  const Item = isFeatured ? FeaturedLink : Link;
  if (children) {
    return (
      <>
        {children.map((child) => (
          <Link
            to={child.path}
            key={child.label}
            onClick={onClick}
            isActive={() => isChildActive && isPathActive(pathname, child.path)}
          >
            {child.label}
          </Link>
        ))}
      </>
    );
  }
  if (isExternalLink(path)) {
    return (
      <Item href={path} as="a" key={label} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {label}
      </Item>
    );
  }
  return (
    <Item to={path} key={label} exact onClick={onClick}>
      {label}
    </Item>
  );
}

export const MobileNavbar: React.FC<{
  adminMode?: boolean;
}> = ({
  adminMode,
}) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen(!open);
  const close = () => setOpen(false);
  const menuItems = useMenuItems();
  const { pathname } = useLocation();
  const activeLabel = findActiveOwnerLabel(pathname, menuItems);
  return (
    <>
      <Spacer />
      <Wrapper>
        <ContainedWidth>
          <Content>
            <Logo onClick={() => setOpen(false)} />
            <Hamburger open={open} onClick={toggle} mobile={isNativePlatform} />
          </Content>
        </ContainedWidth>
      </Wrapper>
      <Portal>
        <Slideable open={open}>
          <Menu>
            <UserMenuWrapper>
              <UserMenu onNavigate={close} />
            </UserMenuWrapper>
            {menuItems.map((menuItem) => (
              <NavMenuItem
                menuItem={menuItem}
                onClick={close}
                isChildActive={activeLabel === menuItem.label}
                pathname={pathname}
                key={menuItem.label}
              />
            ))}
            {/* To avoid CSS conflicts, it's safer to use a regular <a> instead of router link */}
            {adminMode && <Link as="a" href="/admin">Admin</Link>}
          </Menu>
        </Slideable>
      </Portal>
    </>
  );
};
